import type {Event} from '@/apiTypes/event';
import type {
  PaginationMeta,
  PublicEventListDetail,
} from '@/apiTypes/eventList';

const SITE_URL = import.meta.env.VITE_SITE_URL ?? 'https://wayra.cc';

/**
 * Builds a Schema.org JSON-LD object for a public event list page.
 * Uses ItemList wrapping SportsEvent items to enable rich results.
 */
export function buildListJsonLd({
  list,
  events,
  pagination,
}: Pick<PublicEventListDetail, 'list' | 'events' | 'pagination'>) {
  return {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    name: list.name,
    ...(list.description ? {description: list.description} : {}),
    numberOfItems: pagination.totalCount,
    itemListElement: events.map((event, index) =>
      buildListItem(event, index, pagination)
    ),
  };
}

/**
 * Formats a Date as a date-only ISO 8601 string (YYYY-MM-DD) in the
 * event's local timezone when available, falling back to UTC.
 */
function formatDateOnly(date: Date, timezone: string | null): string {
  const d = new Date(date);
  if (timezone) {
    const parts = new Intl.DateTimeFormat('en-CA', {
      timeZone: timezone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).format(d);
    return parts;
  }
  const y = d.getUTCFullYear();
  const m = String(d.getUTCMonth() + 1).padStart(2, '0');
  const day = String(d.getUTCDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function buildListItem(
  event: Event,
  index: number,
  pagination: PaginationMeta,
) {
  const position =
    (pagination.page - 1) * pagination.pageSize + index + 1;

  const item: Record<string, unknown> = {
    '@type': 'SportsEvent',
    name: event.name,
    sport: 'Cycling',
    url: `${SITE_URL}/event/${event.id}`,
    eventStatus: 'https://schema.org/EventScheduled',
    eventAttendanceMode:
      'https://schema.org/OfflineEventAttendanceMode',
  };

  if (event.description) {
    item.description = event.description;
  }

  if (event.startDate) {
    item.startDate = formatDateOnly(event.startDate, event.timezone);
  }

  if (event.endDate) {
    item.endDate = formatDateOnly(event.endDate, event.timezone);
  }

  if (event.heroUrl) {
    item.image = event.heroUrl;
  }

  const hasCoords =
    Number.isFinite(event.lat) && Number.isFinite(event.lng);

  if (event.location || hasCoords) {
    item.location = {
      '@type': 'Place',
      ...(event.location ? {name: event.location} : {}),
      ...(hasCoords
        ? {
            geo: {
              '@type': 'GeoCoordinates',
              latitude: event.lat,
              longitude: event.lng,
            },
          }
        : {}),
    };
  }

  if (event.organizer) {
    const org: Record<string, unknown> = {
      '@type': 'Organization',
      name: event.organizer.name,
    };
    if (event.organizer.website) {
      org.url = event.organizer.website;
    }
    item.organizer = org;
  }

  const lowestPrice = findLowestPrice(event);
  if (lowestPrice) {
    item.offers = {
      '@type': 'Offer',
      price: (lowestPrice.amount / 100).toFixed(2),
      priceCurrency: lowestPrice.currency,
      availability: 'https://schema.org/InStock',
      url: `${SITE_URL}/event/${event.id}`,
    };
  }

  return {
    '@type': 'ListItem',
    position,
    item,
  };
}

function findLowestPrice(event: Event) {
  let lowest: {amount: number; currency: string} | null = null;

  for (const category of event.categories ?? []) {
    for (const price of category.prices ?? []) {
      if (lowest === null || price.amount < lowest.amount) {
        lowest = {amount: price.amount, currency: price.currency};
      }
    }
  }

  return lowest;
}
